/**
 * User Settings Management
 * 
 * Utilities for managing user-level settings in localStorage.
 * Stores personal preferences like mission statement, institution profile, and wizard preferences.
 */

const USER_SETTINGS_KEY = 'snorkle_user_settings';

/**
 * Get default user settings structure
 */
export const getDefaultUserSettings = (userInfo) => ({
  missionStatement: '',
  institutionProfile: '',
  wizardPreferences: {
    educationalLevel: 'general',
    numSlides: 5
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
});

/**
 * Load user settings from localStorage
 */
export const getUserSettings = (userId) => {
  try {
    const key = `${USER_SETTINGS_KEY}_${userId}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    console.error('Error loading user settings:', error);
    return null;
  }
};

/**
 * Save user settings to localStorage
 */
export const saveUserSettings = (userId, settings) => {
  try {
    const key = `${USER_SETTINGS_KEY}_${userId}`;
    const updatedSettings = {
      ...settings,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem(key, JSON.stringify(updatedSettings));
    return updatedSettings;
  } catch (error) {
    console.error('Error saving user settings:', error);
    return null;
  }
};

/**
 * Update specific wizard preference
 */
export const updateWizardPreference = (userId, key, value) => {
  const settings = getUserSettings(userId);
  if (!settings) return null;
  
  const updatedPreferences = {
    ...settings.wizardPreferences,
    [key]: value
  };
  
  return saveUserSettings(userId, {
    ...settings,
    wizardPreferences: updatedPreferences
  });
};

/**
 * Update mission statement
 */
export const updateMissionStatement = (userId, missionStatement) => {
  const settings = getUserSettings(userId);
  if (!settings) return getDefaultUserSettings(userId);
  
  return saveUserSettings(userId, {
    ...settings,
    missionStatement
  });
};

/**
 * Update institution profile
 */
export const updateInstitutionProfile = (userId, institutionProfile) => {
  const settings = getUserSettings(userId);
  if (!settings) return getDefaultUserSettings(userId);
  
  return saveUserSettings(userId, {
    ...settings,
    institutionProfile
  });
};
